/**
 * Job Status Hooks
 * 
 * React hooks for background job monitoring and management
 */

import { useCallback, useEffect, useState } from 'react';
import { JobLog, JobMetrics, JobStatus, jobStatusRepository } from '../repositories/JobStatusRepository';

// Hook for fetching all jobs
export function useJobs(options?: {
    status?: string;
    jobType?: string;
    priority?: string;
    limit?: number;
    offset?: number;
}) {
    const [jobs, setJobs] = useState<JobStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.findAll(options);
            setJobs(data);
        } catch (err) {
            setError(err as Error);
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    return {
        jobs,
        loading,
        error,
        refetch: fetchJobs
    };
}

// Hook for single job with real-time updates
export function useJob(jobId: string | null, options?: {
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [job, setJob] = useState<JobStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { autoRefresh = false, refreshInterval = 5000 } = options || {};

    const fetchJob = useCallback(async () => {
        if (!jobId) {
            setJob(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.findById(jobId);
            setJob(data);
        } catch (err) {
            setError(err as Error);
            setJob(null);
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchJob();
    }, [fetchJob]);

    // Auto-refresh for running jobs
    useEffect(() => {
        if (!autoRefresh || !job || !['queued', 'running', 'retrying'].includes(job.status)) {
            return undefined;
        }

        const interval = setInterval(() => {
            fetchJob();
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [autoRefresh, job, refreshInterval, fetchJob]);

    return {
        job,
        loading,
        error,
        refetch: fetchJob,
        isActive: job ? ['queued', 'running', 'retrying'].includes(job.status) : false
    };
}

// Hook for job logs with live streaming
export function useJobLogs(jobId: string | null, options?: {
    level?: string;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [logs, setLogs] = useState<JobLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { autoRefresh = false, refreshInterval = 2000 } = options || {};

    const fetchLogs = useCallback(async () => {
        if (!jobId) {
            setLogs([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.getLogs(jobId, {
                level: options?.level,
                limit: 100
            });
            setLogs(data);
        } catch (err) {
            setError(err as Error);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [jobId, options?.level]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Auto-refresh for active jobs
    useEffect(() => {
        if (!autoRefresh || !jobId) {
            return undefined;
        }

        const interval = setInterval(() => {
            fetchLogs();
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [autoRefresh, jobId, refreshInterval, fetchLogs]);

    return {
        logs,
        loading,
        error,
        refetch: fetchLogs
    };
}

// Hook for job mutations
export function useJobMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createJob = useCallback(async (jobData: {
        jobType: string;
        name: string;
        description?: string;
        priority?: string;
        maxRetries?: number;
        retryBackoff?: number;
        parameters?: Record<string, any>;
    }): Promise<JobStatus | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.create(jobData);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateProgress = useCallback(async (
        jobId: string,
        progress: number,
        message?: string
    ): Promise<JobStatus | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.updateProgress(jobId, progress, message);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const cancelJob = useCallback(async (jobId: string, reason?: string): Promise<JobStatus | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.cancel(jobId, reason);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const retryJob = useCallback(async (jobId: string): Promise<JobStatus | null> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.retry(jobId);
            return result;
        } catch (err) {
            setError(err as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteJob = useCallback(async (jobId: string): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.delete(jobId);
            return result;
        } catch (err) {
            setError(err as Error);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const cleanupJobs = useCallback(async (options: {
        olderThanDays: number;
        statusFilter?: string[];
        jobTypeFilter?: string[];
    }): Promise<number> => {
        setLoading(true);
        setError(null);

        try {
            const result = await jobStatusRepository.cleanup(options);
            return result;
        } catch (err) {
            setError(err as Error);
            return 0;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createJob,
        updateProgress,
        cancelJob,
        retryJob,
        deleteJob,
        cleanupJobs,
        loading,
        error
    };
}

// Hook for job metrics
export function useJobMetrics(options?: {
    jobType?: string;
    startDate?: string;
    endDate?: string;
}) {
    const [metrics, setMetrics] = useState<JobMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchMetrics = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.getMetrics(options);
            setMetrics(data);
        } catch (err) {
            setError(err as Error);
            setMetrics(null);
        } finally {
            setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        fetchMetrics();
    }, [fetchMetrics]);

    return {
        metrics,
        loading,
        error,
        refetch: fetchMetrics
    };
}

// Hook for job queue status
export function useJobQueue(options?: {
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [queueStatus, setQueueStatus] = useState<{
        totalQueued: number;
        averageWaitTime: number;
        processingCapacity: number;
        estimatedProcessingTime: number;
    } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { autoRefresh = false, refreshInterval = 10000 } = options || {};

    const fetchQueueStatus = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.getQueueStatus();
            setQueueStatus(data);
        } catch (err) {
            setError(err as Error);
            setQueueStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQueueStatus();
    }, [fetchQueueStatus]);

    // Optional auto-refresh with staggered timing
    useEffect(() => {
        if (!autoRefresh) return undefined;

        // Stagger the interval to prevent query storms
        const offset = Math.floor(Math.random() * 2000) + 1000; // 1-3s offset
        let intervalId: NodeJS.Timeout | null = null;

        const timeoutId = setTimeout(() => {
            intervalId = setInterval(fetchQueueStatus, refreshInterval);
        }, offset);

        return () => {
            clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
        };
    }, [autoRefresh, refreshInterval, fetchQueueStatus]);

    return {
        queueStatus,
        loading,
        error,
        refetch: fetchQueueStatus
    };
}

// Hook for running jobs monitoring
export function useRunningJobs(options?: {
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [runningJobs, setRunningJobs] = useState<JobStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { autoRefresh = false, refreshInterval = 5000 } = options || {};

    const fetchRunningJobs = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.findAll({
                status: 'running',
                limit: 20
            });
            setRunningJobs(data);
        } catch (err) {
            setError(err as Error);
            setRunningJobs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRunningJobs();
    }, [fetchRunningJobs]);

    // Optional auto-refresh with staggered timing
    useEffect(() => {
        if (!autoRefresh) return undefined;

        // Stagger the interval to prevent query storms
        const offset = Math.floor(Math.random() * 1500) + 500; // 0.5-2s offset
        let intervalId: NodeJS.Timeout | null = null;

        const timeoutId = setTimeout(() => {
            intervalId = setInterval(fetchRunningJobs, refreshInterval);
        }, offset);

        return () => {
            clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
        };
    }, [autoRefresh, refreshInterval, fetchRunningJobs]);

    return {
        runningJobs,
        loading,
        error,
        refetch: fetchRunningJobs
    };
}

// Hook for failed jobs
export function useFailedJobs() {
    const [failedJobs, setFailedJobs] = useState<JobStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchFailedJobs = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await jobStatusRepository.findAll({
                status: 'failed',
                limit: 50
            });
            setFailedJobs(data);
        } catch (err) {
            setError(err as Error);
            setFailedJobs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFailedJobs();
    }, [fetchFailedJobs]);

    return {
        failedJobs,
        loading,
        error,
        refetch: fetchFailedJobs
    };
}